import { HeroStateConfig } from '@/features/hero/types';

/**
 * Configurable mock system for testing different Hero component states
 * This allows manual configuration of API responses to test specific hero states
 * Organized by ONLINE and OFFLINE modes to correspond with STATES_ONLINE and STATES_OFFLINE
 */

// Mock configurations for ONLINE states (corresponds to STATES_ONLINE)
const ONLINE_CONFIGS: Record<string, HeroStateConfig> = {
  MISSED_1_SESSION: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'MISSED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 2,
            pendingAssessments: [
              { assessmentId: '1', completionStatus: false },
              { assessmentId: '2', completionStatus: false },
            ],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 3600,
    },
  },

  MISSED_2_SESSIONS: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'MISSED' } },
        { name: 'Geometry Fundamentals', attendance: { status: 'MISSED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 3,
            pendingAssessments: [
              { assessmentId: '1', completionStatus: false },
              { assessmentId: '2', completionStatus: false },
              { assessmentId: '3', completionStatus: false },
            ],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 1800,
    },
  },

  MISSED_MULTIPLE_SESSIONS: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'MISSED' } },
        { name: 'Geometry Fundamentals', attendance: { status: 'MISSED' } },
        { name: 'Trigonometry Basics', attendance: { status: 'MISSED' } },
        { name: 'Calculus Introduction', attendance: { status: 'MISSED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 5,
            pendingAssessments: [
              { assessmentId: '1', completionStatus: false },
              { assessmentId: '2', completionStatus: false },
              { assessmentId: '3', completionStatus: false },
              { assessmentId: '4', completionStatus: false },
              { assessmentId: '5', completionStatus: false },
            ],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 900,
    },
  },

  ATTENDED_COMPLETED_0_ASSESSMENTS: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'ATTENDED' } },
        { name: 'Geometry Fundamentals', attendance: { status: 'ATTENDED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 1,
            pendingAssessments: [
              { assessmentId: '1', completionStatus: false },
            ],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 7200,
    },
  },

  ATTENDED_LEFT_WITH_ATLEAST_1_ASSESSMENT: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'ATTENDED' } },
        { name: 'Geometry Fundamentals', attendance: { status: 'ATTENDED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 2,
            pendingAssessments: [
              { assessmentId: '1', completionStatus: false },
              { assessmentId: '2', completionStatus: false },
            ],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 10_800,
    },
  },

  ATTENDED_LEFT_WITH_NO_ASSESSMENT: {
    modules: {
      sessions: [
        { name: 'Mathematics Basics', attendance: { status: 'ATTENDED' } },
        { name: 'Algebra Introduction', attendance: { status: 'ATTENDED' } },
        { name: 'Geometry Fundamentals', attendance: { status: 'ATTENDED' } },
      ],
    },
    assessments: {
      onGoingModules: [
        {
          id: '1',
          name: 'Math Module',
          status: 'ongoing',
          pendingTasks: {
            count: 0,
            pendingAssessments: [],
          },
        },
      ],
    },
    sessions: {
      hasUpcomingSession: true,
      remaining_seconds: 14_400,
    },
  },
};

// Mock configurations for OFFLINE states (corresponds to STATES_OFFLINE)
const OFFLINE_CONFIGS: Record<string, HeroStateConfig> = {
  ATTENDED_NO_SESSION: {
    modules: {
      sessions: [
        {
          name: 'Welcome Lesson',
          delivery_type: 'offline',
        },
      ],
    },
    assessments: {
      onGoingModules: [],
    },
    sessions: {
      hasUpcomingSession: false,
      remaining_seconds: 0,
    },
  },

  ATTENDED_PREVIOUS_SESSION: {
    modules: {
      sessions: [
        {
          name: 'Algebra Fundamentals',
          attendance: { status: 'ATTENDED' },
          delivery_type: 'offline',
          topics: [{ name: 'Basic Equations' }],
        },
      ],
    },
    assessments: {
      onGoingModules: [],
    },
    sessions: {
      hasUpcomingSession: false,
      remaining_seconds: 0,
    },
  },

  MISSED_PREVIOUS_SESSION: {
    modules: {
      sessions: [
        {
          name: 'Algebra Fundamentals',
          attendance: { status: 'MISSED' },
          delivery_type: 'offline',
          topics: [{ name: 'Basic Equations' }],
        },
      ],
    },
    assessments: {
      onGoingModules: [],
    },
    sessions: {
      hasUpcomingSession: false,
      remaining_seconds: 0,
    },
  },

  COMPLETED_MODULE: {
    modules: {
      sessions: [
        {
          name: 'Algebra Fundamentals',
          attendance: { status: 'ATTENDED' },
          delivery_type: 'offline',
          topics: [{ name: 'Advanced Equations' }],
        },
      ],
      module: {
        name: 'Advanced Algebra',
        status: 'PAID_COMPLETED',
      },
    },
    assessments: {
      onGoingModules: [],
    },
    sessions: {
      hasUpcomingSession: false,
      remaining_seconds: 0,
    },
  },
};

// Combined configurations for both online and offline states
export const HERO_STATE_CONFIGS: Record<string, HeroStateConfig> = {
  ...ONLINE_CONFIGS,
  ...OFFLINE_CONFIGS,
};

// Current active configuration (can be changed for testing)
export const currentConfig: HeroStateConfig = ONLINE_CONFIGS.MISSED_1_SESSION;
